import React from 'react';
import { PropTypes } from 'prop-types';
import { Icons, Button } from 'sarsaparilla';
import * as questionnaire from './questionaire';
import SingleChoiceRadioQuestion from './SingleChoiceRadioQuestion';
import QuestionnaireStart from './QuestionnaireStart';
import QuestionsSummary from './QuestionsSummary';
import QuestionnaireResult from './QuestionnaireResult';
import QuestionnaireProgressBar from './QuestionnaireProgressBar';

const propTypes = {
  permit: PropTypes.object.isRequired,
  pActions: PropTypes.object.isRequired,
  lotteries: PropTypes.array
};

const defaultProps = {};

export default class QuestionnaireContainer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      currentQuestion: null,
      currentAnswer: null,
      answers: [],
      questions: [],
      currentQuestionIndex: null,
      questionnaireResult: null
    };
    this.onAnswer = this.onAnswer.bind(this);
    this.onPrevious = this.onPrevious.bind(this);
    this.onStart = this.onStart.bind(this);
    this.onNext = this.onNext.bind(this);
    this.onRestart = this.onRestart.bind(this);
    this.onAnswerEdit = this.onAnswerEdit.bind(this);
  }

  onPrevious() {
    const currentQuestionIndex = this.state.currentQuestionIndex - 1;
    if (currentQuestionIndex >= 0) {
      const currentQuestion = this.state.questions[currentQuestionIndex];
      const answers = questionnaire.getAnswersById(this.state.answers);
      const currentAnswer = answers[currentQuestion.id];
      const questions = this.state.questions.slice(0, currentQuestionIndex + 1);
      this.setState({
        currentQuestionIndex,
        currentQuestion,
        questionnaireResult: null,
        currentAnswer,
        questions
      });
    }
    else {
      this.onRestart();
    }
  }

  onStart() {
    const answers = [];
    const question = questionnaire.getNextQuestion(null, answers);
    const questions = [];
    questions.push(question);
    const currentQuestionIndex = questions.length - 1;
    this.setState({
      answers,
      currentQuestion: question,
      questions,
      currentQuestionIndex,
      currentAnswer: null
    });
  }

  onRestart() {
    const answers = [];
    const questions = [];
    this.setState({
      answers,
      currentQuestion: null,
      questionnaireResult: null,
      questions,
      currentQuestionIndex: null,
      currentAnswer: null
    });
  }

  onNext() {
    if (!this.state.currentAnswer) {
      return;
    }
    const answers = this.state.answers;
    answers.push(this.state.currentAnswer);
    const result = questionnaire.getNextQuestion(this.state.currentQuestion, this.state.answers);
    if (typeof result === 'string' || result instanceof String) {
      this.setState({
        answers,
        currentQuestion: null,
        questionnaireResult: result,
        currentAnswer: null,
        currentQuestionIndex: this.state.questions.length
      });
    }
    else {
      const questions = this.state.questions;
      questions.push(result);
      const currentQuestionIndex = questions.length - 1;
      const currentAnswer = questionnaire.getAnswersById(this.state.answers)[result.id];

      this.setState({
        answers,
        currentQuestion: result,
        questions,
        currentQuestionIndex,
        currentAnswer
      });
    }
  }

  onAnswer(choice) {
    const answer = Object.assign({}, choice);
    answer.id = this.state.currentQuestion.id;
    this.setState({
      currentAnswer: answer
    });
  }

  onAnswerEdit(answer, question) {
    let currentQuestionIndex = this.state.currentQuestionIndex;
    let currentQuestion = this.state.currentQuestion;
    this.state.questions.forEach((q, index) => {
      if (q.id === question.id) {
        currentQuestionIndex = index;
        currentQuestion = q;
      }
    });
    const answers = questionnaire.getAnswersById(this.state.answers);
    const currentAnswer = answers[currentQuestion.id];
    const questions = this.state.questions.slice(0, currentQuestionIndex + 1);
    this.setState({
      currentQuestionIndex,
      currentQuestion,
      questionnaireResult: null,
      currentAnswer,
      questions
    });
  }

  renderContent() {
    if (!this.state.currentQuestion && this.state.questionnaireResult) {
      return (
        <div className="rec-questionnaire-question-container">
          <button
            className="rec-button-link"
            title="Start Over"
            onClick={this.onRestart}
            aria-label="Start Over">
            <Icons.IconReplay /> Start Over
          </button>
          <QuestionnaireResult
            permit={this.props.permit}
            result={this.state.questionnaireResult}
            lotteries={this.props.lotteries}
            pActions={this.props.pActions} />
        </div>
      );
    }

    if (this.state.currentQuestion) {
      return (
        <div className="rec-questionnaire-question-container">
          {this.state.currentQuestion.type === 'single-choice-radio' &&
          <SingleChoiceRadioQuestion
            question={this.state.currentQuestion}
            answer={this.state.currentAnswer}
            onSelect={this.onAnswer} />
          }
        </div>
      );
    }

    return (
      <QuestionnaireStart
        permit={this.props.permit}
        onStart={this.onStart} />
    );
  }

  renderQuestionsHeader() {
    if (this.state.currentQuestion) {
      return (
        <h1 className="rec-questionnaire-subtitle">
          Question {(this.state.currentQuestionIndex + 1).toLocaleString('en-US', {
            minimumIntegerDigits: 2,
            useGrouping: false
          })}
        </h1>
      );
    }
    if (this.state.currentQuestionIndex === null) {
      return (
        <h1 className="rec-questionnaire-subtitle">
          Introduction
        </h1>
      );
    }
    return null;
  }

  renderQuestionsNavigation() {
    if (this.state.currentQuestion) {
      return (
        <div className="rec-questionnaire-navigation">
          <Button
            className="rec-button-tertiary"
            title="Navigate to previous question"
            onClick={this.onPrevious}
            aria-label="Navigate to previous question">
            <Icons.IconArrowBack /> Previous
          </Button>

          <Button
            className="rec-button-primary"
            title="Navigate to next question"
            disabled={!this.state.currentAnswer}
            onClick={this.onNext}
            aria-label="Navigate to next question">
            Next <Icons.IconArrowForward />
          </Button>
        </div>
      );
    }
    return null;
  }

  renderQuestionsSummary() {
    if (this.state.currentQuestion || this.state.questionnaireResult) {
      return (
        <QuestionsSummary
          questions={this.state.questions}
          answers={this.state.answers}
          onAnswerEdit={this.onAnswerEdit} />
      );
    }
    return null;
  }

  renderPogressBar() {
    let progress = 10;
    if (this.state.questionnaireResult) {
      progress = 100;
    }
    else if (this.state.currentQuestionIndex != null) {
      progress = ((this.state.currentQuestionIndex * 1.0) / (questionnaire.questions.length)) * 100;
      if (progress < 20) {
        progress = 20;
      }
    }
    else {
      progress = 10;
    }
    return (
      <QuestionnaireProgressBar
        progress={progress} />
    );
  }

  render() {
    return (
      <div className="rec-questionnaire">
        {this.renderPogressBar()}
        {this.renderQuestionsHeader()}
        {this.renderContent()}
        {this.renderQuestionsNavigation()}
        <hr />
        {this.renderQuestionsSummary()}
      </div>
    );
  }
}

QuestionnaireContainer.displayName = 'QuestionnaireContainer';
QuestionnaireContainer.propTypes = propTypes;
QuestionnaireContainer.defaultProps = defaultProps;
